import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api } from "../../data";
import { useAccount } from "../../app/session";
import { authFehler, authFehlerArt } from "../konto/auth";
import { CaptchaFeld, captchaConfig } from "../konto/Captcha";
import { istTeamEinladung, internWeiter } from "../../lib/bereich";
import { InternRahmen } from "./InternRahmen";

export const KEIN_INTERNER_ZUGANG = "Dieses Konto hat keinen internen Zugang.";
/** Falsches Passwort, unbekannte oder unbestätigte Adresse: immer dieselbe Meldung (verrät nicht, ob es ein Konto gibt) */
export const ANMELDUNG_FEHLGESCHLAGEN = "E-Mail oder Passwort stimmen nicht.";
export const LINK_UNTERWEGS = "Wenn es zu dieser Adresse einen Zugang gibt, ist der Anmeldelink unterwegs. Prüfe auch den Spam-Ordner.";

/** Fehlertext der internen Anmeldung: nur Sicherheitsprüfung, Grenzen und Netz im Klartext, sonst neutral */
export function internAnmeldeFehler(err: unknown): string {
  const art = authFehlerArt(err);
  if (art === "captcha" || art === "rate_limit" || art === "mail_rate_limit" || art === "network") return authFehler(err, "de");
  return ANMELDUNG_FEHLGESCHLAGEN;
}

/** Nach der Anmeldung: weiter in den internen Bereich, abweisen oder (Einladung ins Team) auch ohne Teamzugang weiter */
export function internZugangsEntscheidung(a: { isPlatformStaff?: boolean } | undefined, weiter: string): "weiter" | "abweisen" | "warten" {
  if (!a) return "warten";
  if (a.isPlatformStaff || istTeamEinladung(weiter)) return "weiter";
  return "abweisen";
}

/**
 * Anmeldung des internen Bereichs (/intern/anmelden, Inhaber 03.11.2026): eigene Sitzung (lib/bereich.ts), nur E-Mail und
 * Passwort bzw. Anmeldelink, „Passwort vergessen“ mit Rückweg nach /intern. Danach prüft die App, ob das Konto zum Team gehört
 * (platform_admins bzw. aktive Zeile in platform_staff, wie app.is_platform_staff); sonst wird nur die interne Sitzung
 * beendet und neutral gemeldet. Den zweiten Faktor fragt danach ZweiterFaktorGate ab, die Einrichtung BetriebFaktorPflicht.
 */
export function InternAnmelden() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const weiter = internWeiter(params.get("weiter"));
  const { session, account, loading, error: kontoFehler } = useAccount();
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [modus, setModus] = useState<"passwort" | "link">("passwort");
  const [email, setEmail] = useState("");
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaRunde, setCaptchaRunde] = useState(0);
  const mitCaptcha = api.mode !== "demo" && !!captchaConfig();
  const abgewiesen = useRef<string | null>(null);

  // Angemeldet (Passwort, Anmeldelink aus der Mail oder schon vorhandene interne Sitzung): Zugang prüfen
  const entscheidung = session ? internZugangsEntscheidung(account, weiter) : null;
  useEffect(() => {
    if (!session) return;
    if (entscheidung === "weiter") navigate(weiter, { replace: true });
    else if (entscheidung === "abweisen" && abgewiesen.current !== session.userId) {
      abgewiesen.current = session.userId;
      // nur die interne Sitzung (eigener Speicherschlüssel); eine Anmeldung im Kundenportal bleibt bestehen
      void api.signOut().catch(() => undefined);
      setFehler(KEIN_INTERNER_ZUGANG);
      setBusy(false);
    }
  }, [session, entscheidung, weiter, navigate]);
  useEffect(() => {
    if (session && kontoFehler) {
      void api.signOut().catch(() => undefined);
      setFehler("Der Zugang konnte gerade nicht geprüft werden. Bitte gleich noch einmal anmelden.");
      setBusy(false);
    }
  }, [session, kontoFehler]);

  async function anmelden(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const mail = String(fd.get("email") ?? "").trim();
    if (mitCaptcha && !captcha) { setFehler("Bitte zuerst die Sicherheitsprüfung lösen."); return; }
    setBusy(true);
    setFehler(null);
    setMeldung(null);
    abgewiesen.current = null;
    try {
      if (modus === "link") {
        if (!api.requestLoginLink) throw new Error("Anmeldelinks gibt es hier nicht.");
        await api.requestLoginLink(mail, { redirectPath: `/intern/anmelden?weiter=${encodeURIComponent(weiter)}`, captchaToken: captcha });
        setMeldung(LINK_UNTERWEGS);
        setBusy(false);
      } else {
        await api.signInWithPassword(mail, String(fd.get("password") ?? ""), { captchaToken: captcha });
        // weiter geht es über die Prüfung oben, sobald das Konto geladen ist
      }
    } catch (err) {
      setFehler(internAnmeldeFehler(err));
      setBusy(false);
    } finally {
      if (mitCaptcha) { setCaptcha(null); setCaptchaRunde((n) => n + 1); }
    }
  }

  const pruefen = !!session && (loading || entscheidung === "weiter");
  return (
    <InternRahmen>
      <div className="co-intern-karte">
        <h1>Interne Anmeldung</h1>
        <p className="co-intern-unter">Nur für Mitarbeiterinnen und Mitarbeiter von CreatorOffice.</p>
        {pruefen ? <p className="muted" role="status">Zugang wird geprüft …</p> : (
          <form onSubmit={(e) => void anmelden(e)} aria-busy={busy}>
            <label>
              E-Mail
              <input name="email" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            {modus === "passwort" && (
              <label>
                Passwort
                <input name="password" type="password" autoComplete="current-password" required={api.mode !== "demo"} />
              </label>
            )}
            {mitCaptcha && <CaptchaFeld onToken={setCaptcha} resetKey={captchaRunde} />}
            {fehler && <p className="tk-error" role="alert">{fehler}</p>}
            {meldung && <p className="notice" role="status">{meldung}</p>}
            <button className="primary" type="submit" disabled={busy} aria-busy={busy}>
              {busy ? (modus === "link" ? "Wird gesendet …" : "Wird angemeldet …") : modus === "link" ? "Anmeldelink senden" : "Anmelden"}
            </button>
            <div className="co-intern-wege">
              <Link to="/intern/passwort" state={{ email }}>Passwort vergessen?</Link>
              {api.requestLoginLink && (modus === "passwort"
                ? <button type="button" onClick={() => { setModus("link"); setFehler(null); setMeldung(null); }}>Anmeldelink per Mail</button>
                : <button type="button" onClick={() => { setModus("passwort"); setFehler(null); setMeldung(null); }}>Mit Passwort anmelden</button>)}
            </div>
          </form>
        )}
      </div>
    </InternRahmen>
  );
}
